import type { ReactNode } from 'react'

/**
 * One form field with its label, hint and error.
 *
 * Accessibility is the reason this exists rather than raw markup at each call site
 * (01 §8, WCAG 2.2 AA): the error is wired to the input with `aria-describedby` and announced
 * with `role="alert"`, and the label is always a real `<label>` — a placeholder is not a label.
 */
export function Field({
  id,
  label,
  hint,
  error,
  required,
  children,
}: {
  id: string
  label: string
  hint?: string
  error?: string
  required?: boolean
  children: (props: {
    id: string
    'aria-describedby': string | undefined
    'aria-invalid': boolean | undefined
    'aria-required': boolean | undefined
  }) => ReactNode
}) {
  const hintId = hint ? `${id}-hint` : undefined
  const errorId = error ? `${id}-error` : undefined
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined

  return (
    <div className="grid gap-1.5">
      <label htmlFor={id} className="text-sm font-semibold text-ink">
        {label}
        {!required && <span className="ml-1.5 font-normal text-muted">(optional)</span>}
      </label>
      {hint && (
        <p id={hintId} className="m-0 text-sm text-muted">
          {hint}
        </p>
      )}
      {children({
        id,
        'aria-describedby': describedBy,
        'aria-invalid': error ? true : undefined,
        'aria-required': required || undefined,
      })}
      {error && (
        <p id={errorId} role="alert" className="m-0 text-sm font-medium text-blue-press">
          {error}
        </p>
      )}
    </div>
  )
}

export const inputClass =
  'w-full rounded-btn border border-line-2 bg-white px-3.5 py-2.5 text-base text-ink ' +
  'placeholder:text-muted focus:border-blue focus:outline-none aria-[invalid=true]:border-blue-press'
